import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Package,
  Truck,
  AlertCircle,
  LogOut,
  RefreshCw,
  Info,
  UserCog,
  Printer
} from 'lucide-react';

import { http } from '../../lib/http';

const PackagingDashboard = ({ onSwitchDashboard }) => {
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [packagingOverview, setPackagingOverview] = useState({});
  const [serviceMeta, setServiceMeta] = useState({ service_date: '', next_service_date: '', service_day: '', is_preview: false });
  const [selectedRoute, setSelectedRoute] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await http.get('/packaging/overview');
      const data = res.data;
      setPackagingOverview(data.overview || {});
      setServiceMeta({
        service_date: data.service_date || '',
        next_service_date: data.next_service_date || '',
        service_day: data.service_day || '',
        is_preview: !!data.is_preview
      });
    } catch (err) {
      console.error('Error loading packaging data:', err);
      setError('Failed to connect to operations server. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 300000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Auto-select first route on data load, or reset if selected route no longer exists
  useEffect(() => {
    const routeNames = Object.keys(packagingOverview);
    if (routeNames.length > 0 && (!selectedRoute || !routeNames.includes(selectedRoute))) {
      setSelectedRoute(routeNames[0]);
    }
  }, [packagingOverview, selectedRoute]);

  const customers = selectedRoute ? (packagingOverview[selectedRoute] || []) : [];

  if (loading && Object.keys(packagingOverview).length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <RefreshCw className="w-10 h-10 text-orange-600 animate-spin mx-auto mb-4" />
          <p className="font-semibold text-gray-600">Loading Packaging Dispatch...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 sticky top-0 z-10 shadow-sm">
        <div className="flex items-center justify-between max-w-[1600px] mx-auto">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-orange-600 rounded-xl">
              <Package className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Packaging Dashboard</h1>
              <p className="text-xs text-gray-500">
                {serviceMeta.next_service_date
                  ? `Packaging for ${serviceMeta.service_day || ''} ${serviceMeta.next_service_date}`.trim()
                  : serviceMeta.service_date
                    ? `${serviceMeta.service_day || ''} ${serviceMeta.service_date}`.trim()
                    : new Date().toLocaleDateString('en-CA', { weekday: 'long', month: 'short', day: 'numeric' })}
                {serviceMeta.is_preview && ' — Preview'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button onClick={fetchData} variant="outline" size="sm" className="gap-2 print:hidden">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Sync
            </Button>
            <Button onClick={() => window.print()} variant="outline" size="sm" className="gap-2 print:hidden">
              <Printer className="w-4 h-4" />
              Print Labels
            </Button>
            {onSwitchDashboard && (
              <Button
                onClick={() => onSwitchDashboard('selector')}
                variant="outline"
                size="sm"
                className="gap-2 border-orange-500 text-orange-600 hover:bg-orange-50"
              >
                <UserCog className="w-4 h-4" />
                <span className="hidden sm:inline">Switch</span>
              </Button>
            )}
            <Button onClick={logout} variant="destructive" size="sm" className="gap-2">
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto p-6 space-y-5">
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {serviceMeta.is_preview && (
          <Alert className="border-blue-200 bg-blue-50">
            <Info className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-800">
              Weekend detected. Showing the next service day schedule.
            </AlertDescription>
          </Alert>
        )}

        {Object.keys(packagingOverview).length === 0 ? (
          <div className="bg-white border-2 border-dashed border-gray-200 rounded-xl p-16 text-center">
            <Truck className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-400">No Routes Assigned Today</h3>
            <p className="text-sm text-gray-400 mt-1">Check with Admin if customers haven't been assigned to routes.</p>
          </div>
        ) : (
          <>
            {/* Route Selector */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Truck className="w-4 h-4 text-gray-400" />
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Select Route</h2>
              </div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(packagingOverview).map(([routeName, routeCustomers]) => {
                  const activeCount = routeCustomers.filter(c => c.active_next_day !== false).length;
                  const isSelected = selectedRoute === routeName;
                  return (
                    <Button
                      key={routeName}
                      variant={isSelected ? 'default' : 'outline'}
                      onClick={() => setSelectedRoute(routeName)}
                      className={isSelected ? 'bg-orange-600 hover:bg-orange-700 text-white' : ''}
                    >
                      {routeName}
                      <Badge variant="secondary" className="ml-2 text-xs">
                        {activeCount}
                      </Badge>
                    </Button>
                  );
                })}
              </div>
            </div>

            {/* Customer Table for Selected Route */}
            {selectedRoute && customers.length > 0 && (
              <Card className="overflow-hidden">
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader className="bg-gray-50">
                        <TableRow>
                          <TableHead className="w-[60px] text-center text-xs font-medium text-gray-500">Stop</TableHead>
                          <TableHead className="text-xs font-medium text-gray-500">Customer</TableHead>
                          <TableHead className="text-xs font-medium text-gray-500">Preference</TableHead>
                          <TableHead className="text-xs font-medium text-gray-500">Meal Type</TableHead>
                          <TableHead className="w-[80px] text-center text-xs font-medium text-gray-500">NV/Day</TableHead>
                          <TableHead className="w-[60px] text-center text-xs font-medium text-gray-500">Bags</TableHead>
                          <TableHead className="text-xs font-medium text-gray-500">Add-ons</TableHead>
                          <TableHead className="w-[100px] text-center text-xs font-medium text-gray-500">B / L / D</TableHead>
                          <TableHead className="text-xs font-medium text-gray-500">Allergies</TableHead>
                          <TableHead className="text-xs font-medium text-gray-500">Special</TableHead>
                          <TableHead className="text-xs font-medium text-gray-500">Dietary Notes</TableHead>
                          <TableHead className="text-xs font-medium text-gray-500">Packaging Notes</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {customers.map((cx) => {
                          const notDelivering = cx.active_next_day === false;
                          const isNew = cx.active_today === false && cx.active_next_day === true;
                          return (
                          <TableRow
                            key={cx.id}
                            className={
                              notDelivering ? 'bg-red-50/50 opacity-60' :
                              cx.status === 'paused' ? 'bg-red-50/50 opacity-60' : ''
                            }
                          >
                            <TableCell className="text-center font-semibold text-gray-400">
                              {cx.stop_no || '-'}
                            </TableCell>
                            <TableCell>
                              <div>
                                <div className="flex items-center gap-1.5">
                                  <span className="font-semibold text-gray-900">{cx.name}</span>
                                  {cx.status === 'paused' && (
                                    <Badge variant="destructive" className="text-[10px]">PAUSED</Badge>
                                  )}
                                  {notDelivering && cx.status !== 'paused' && (
                                    <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px]">LAST DAY</Badge>
                                  )}
                                  {isNew && (
                                    <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-[10px]">NEW</Badge>
                                  )}
                                </div>
                                {cx.status_note && (
                                  <p className={`text-[11px] mt-0.5 ${
                                    isNew ? 'text-blue-600' : 'text-amber-600'
                                  }`}>{cx.status_note}</p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              {cx.preference !== '-' ? (
                                <span className="text-sm text-gray-700">{cx.preference}</span>
                              ) : (
                                <span className="text-gray-300">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-col gap-1">
                                {cx.today_meal_type === 'Chicken' ? (
                                  <Badge className="bg-red-100 text-red-700 border-red-200 w-fit">Non-Veg</Badge>
                                ) : (
                                  <Badge className="bg-green-100 text-green-700 border-green-200 w-fit">Veg</Badge>
                                )}
                                <span className="text-xs text-gray-400">{cx.preferences.nv_preference}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant="outline" className={
                                cx.nv_meal_count === 2 ? 'bg-red-50 text-red-700 border-red-200' :
                                cx.nv_meal_count === 1 ? 'bg-orange-50 text-orange-700 border-orange-200' :
                                'bg-gray-50 text-gray-400 border-gray-200'
                              }>
                                {cx.nv_meal_count}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-center">
                              {cx.bag_count > 0 ? (
                                <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                                  {cx.bag_count}
                                </Badge>
                              ) : (
                                <span className="text-gray-300">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {cx.add_ons !== '-' ? (
                                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs">
                                  {cx.add_ons}
                                </Badge>
                              ) : <span className="text-gray-300">-</span>}
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="flex justify-center gap-1">
                                {['breakfast', 'lunch', 'dinner'].map(meal => {
                                  const val = cx.meals[meal];
                                  const label = meal[0].toUpperCase();
                                  return (
                                    <Badge key={meal} className={
                                      val === 'non-veg' ? 'bg-red-500 text-white' :
                                      val === 'veg'     ? 'bg-green-500 text-white' :
                                                          'bg-gray-200 text-gray-400'
                                    }>{label}</Badge>
                                  );
                                })}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-col gap-0.5">
                                {cx.preferences.allergy_info !== '-' && (
                                  <span className="text-sm text-red-700"><span className="font-semibold">Allergies:</span> {cx.preferences.allergy_info}</span>
                                )}
                                {cx.preferences.avoid !== '-' && (
                                  <span className="text-sm text-orange-700"><span className="font-semibold">Avoid:</span> {cx.preferences.avoid}</span>
                                )}
                                {cx.preferences.allergy_info === '-' && cx.preferences.avoid === '-' && (
                                  <span className="text-gray-300">-</span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              {cx.special_instruction !== '-' ? (
                                <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300 text-[10px]">{cx.special_instruction}</Badge>
                              ) : (
                                <span className="text-gray-300">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {cx.dietary_notes !== '-' ? (
                                <span className="text-sm text-gray-700">{cx.dietary_notes}</span>
                              ) : (
                                <span className="text-gray-300">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {cx.internal_notes !== '-' ? (
                                <span className="text-sm text-gray-600 italic">{cx.internal_notes}</span>
                              ) : (
                                <span className="text-gray-300">-</span>
                              )}
                            </TableCell>
                          </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Print-only labels (hidden on screen, visible when printing) */}
            <div id="printable-labels" className="hidden print:block">
              <div className="text-center mb-4 text-sm font-semibold">
                {selectedRoute} — {serviceMeta.next_service_date || serviceMeta.service_date}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                {customers
                  .filter(cx => cx.active_next_day !== false && cx.status !== 'paused')
                  .flatMap(cx => {
                    const count = Math.max(cx.bag_count || 1, 1);
                    return Array.from({ length: count }, (_, i) => ({ ...cx, _bagIndex: i, _bagTotal: count }));
                  })
                  .map((cx, idx) => (
                    <div key={idx} style={{ border: '1px solid #888', borderRadius: '4px', padding: '6px', fontSize: '11px', lineHeight: '1.3', breakInside: 'avoid' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: '13px' }}>
                        <span>{cx.name}</span>
                        <span>#{cx.stop_no || '-'}</span>
                      </div>
                      {cx._bagTotal > 1 && (
                        <div style={{ fontSize: '10px', fontWeight: '600', color: '#666' }}>
                          Bag {cx._bagIndex + 1} of {cx._bagTotal}
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: '6px', marginTop: '3px' }}>
                        {['breakfast', 'lunch', 'dinner'].filter(m => cx.meals[m] && cx.meals[m] !== 'none').map(m => (
                          <span key={m} style={{ fontWeight: '700', color: cx.meals[m] === 'non-veg' ? '#b91c1c' : '#15803d' }}>
                            {m[0].toUpperCase()}
                          </span>
                        ))}
                      </div>
                      {cx.add_ons !== '-' && <div style={{ marginTop: '2px' }}><span style={{ fontWeight: '600' }}>Add-ons:</span> {cx.add_ons}</div>}
                      {cx.preferences.allergy_info !== '-' && <div style={{ marginTop: '2px', color: '#b91c1c' }}><span style={{ fontWeight: '600' }}>Allergies:</span> {cx.preferences.allergy_info}</div>}
                      {cx.preferences.avoid !== '-' && <div style={{ color: '#c2410c' }}><span style={{ fontWeight: '600' }}>Avoid:</span> {cx.preferences.avoid}</div>}
                      {cx.special_instruction !== '-' && <div style={{ marginTop: '2px', color: '#854d0e', fontWeight: '600' }}>Special: {cx.special_instruction}</div>}
                      {cx.dietary_notes !== '-' && <div style={{ marginTop: '2px' }}><span style={{ fontWeight: '600' }}>Diet:</span> {cx.dietary_notes}</div>}
                      {cx.internal_notes !== '-' && <div style={{ fontStyle: 'italic', color: '#666' }}><span style={{ fontWeight: '600' }}>Pkg:</span> {cx.internal_notes}</div>}
                    </div>
                  ))}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #printable-labels, #printable-labels * { visibility: visible; }
          #printable-labels {
            position: absolute; left: 0; top: 0; width: 100%;
            padding: 10mm;
          }
          .print\\:hidden { display: none !important; }
          .hidden.print\\:block { display: block !important; visibility: visible !important; }
        }
      `}</style>
    </div>
  );
};

export default PackagingDashboard;
